package com.schoolconnect.app.feature.parents

import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import com.schoolconnect.app.datasource.network.response.SchoolStatisticsResponse
import com.schoolconnect.app.util.isValidEmail
import com.schoolconnect.app.util.isValidMobileNumber

data class ParentsUiState(
    val isLoading: Boolean = false,
    val isPageLoading: Boolean = false,
    val isAnalyticsLoading: Boolean = false,
    val isSaving: Boolean = false,
    val errorMessage: String = "",
    val apiErrorMessage: String = "",
    val infoMessage: String = "",
    val searchQuery: String = "",
    val filters: ParentFilterCriteria = ParentFilterCriteria(),
    val sortField: ParentSortField = ParentSortField.ADMISSION_NO,
    val isSortAscending: Boolean = true,
    val pageIndex: Int = 0,
    val pageSize: Int = com.schoolconnect.app.feature.common.PaginationPolicy.DEFAULT_PAGE_SIZE,
    val totalCount: Int = 0,
    val isTotalCountExact: Boolean = false,
    val parents: List<ParentRowUi> = emptyList(),
    val studentOptions: List<ParentStudentOptionUi> = emptyList(),
    val studentPickerQuery: String = "",
    val studentPickerPageIndex: Int = 0,
    val studentPickerTotalCount: Int = 0,
    val studentPickerIsLoading: Boolean = false,
    val studentPickerIsLoadingMore: Boolean = false,
    val studentPickerHasMore: Boolean = true,
    val studentPickerError: String = "",
    val analytics: SchoolStatisticsResponse? = null,
    val filterClassSectionOptions: List<ParentFilterClassSectionOption> = emptyList(),
    val filterClassOptions: List<String> = emptyList(),
    val filterSectionOptions: List<String> = emptyList(),
    val filterSectionsByClass: Map<String, List<String>> = emptyMap(),
    val isFilterLookupLoading: Boolean = false,
    val filterLookupError: String = "",
    val mutationSuccessNonce: Long = 0L
)

sealed interface ParentsUiEvent {
    data object BackClicked : ParentsUiEvent
    data object RefreshRequested : ParentsUiEvent
    data class SearchChanged(val query: String) : ParentsUiEvent
    data class FiltersApplied(val filters: ParentFilterCriteria) : ParentsUiEvent
    data object FiltersReset : ParentsUiEvent
    data class SortChanged(val field: ParentSortField) : ParentsUiEvent
    data class PageChanged(val pageIndex: Int) : ParentsUiEvent
    data class PageSizeChanged(val pageSize: Int) : ParentsUiEvent
    data object StudentPickerOpened : ParentsUiEvent
    data class StudentPickerSearchChanged(val query: String) : ParentsUiEvent
    data object StudentPickerLoadMore : ParentsUiEvent
    data class SaveParent(val draft: ParentEditorDraft) : ParentsUiEvent
    data object ParentEditorDismissed : ParentsUiEvent
    data object ClearMessages : ParentsUiEvent
}

data class ParentRowUi(
    val id: String,
    val admissionNo: String,
    val studentId: String,
    val studentName: String,
    val className: String,
    val section: String,
    val rollNo: String,
    val linkedStudents: List<ParentLinkedStudentUi> = emptyList(),
    val primaryContactName: String,
    val primaryContactMobile: String,
    val primaryContactEmail: String,
    val primaryRelationshipCode: String,
    val secondaryContactName: String,
    val secondaryContactMobile: String,
    val secondaryContactEmail: String,
    val secondaryRelationshipCode: String,
    val statusCode: String,
    val createdBy: String,
    val createdAtText: String,
    val updatedBy: String,
    val updatedAtText: String,
    val createdAtMillis: Long,
    val updatedAtMillis: Long,
    val primaryLinkId: String = "",
    val primaryLinkIsActive: Boolean = false,
    val secondaryParentId: String = "",
    val secondaryLinkId: String = ""
)

data class ParentLinkedStudentUi(
    val studentId: String,
    val studentName: String,
    val admissionNo: String,
    val className: String,
    val section: String,
    val relationshipType: String,
    val isPrimaryContact: Boolean
)

data class ParentStudentOptionUi(
    val id: String,
    val admissionNo: String,
    val studentName: String,
    val className: String,
    val section: String,
    val rollNo: String,
    val statusCode: String,
    val parents: List<com.schoolconnect.app.datasource.network.response.StudentParentListResponse> = emptyList()
)

data class ParentFilterClassSectionOption(
    val id: String,
    val className: String,
    val sectionName: String
) {
    val label: String get() = "$className - $sectionName"
}

data class ParentEditorDraft(
    val id: String = "",
    val selectedStudentId: String = "",
    val selectedStudentLabel: String = "",
    val studentSearchQuery: String = "",
    val primaryContactName: String = "",
    val primaryContactMobile: String = "",
    val primaryContactEmail: String = "",
    val primaryRelationshipCode: String = SchoolKeys.V_REL_FATHER,
    val secondaryContactName: String = "",
    val secondaryContactMobile: String = "",
    val secondaryContactEmail: String = "",
    val secondaryRelationshipCode: String = SchoolKeys.V_REL_MOTHER,
    val statusCode: String = SchoolKeys.V_STATUS_ACTIVE,
    val primaryLinkId: String = "",
    val secondaryParentId: String = "",
    val secondaryLinkId: String = ""
)

data class ParentEditorValidationErrors(
    val student: String = "",
    val primaryName: String = "",
    val primaryMobile: String = "",
    val primaryEmail: String = "",
    val secondaryName: String = "",
    val secondaryMobile: String = "",
    val secondaryEmail: String = ""
) {
    val hasAnyError: Boolean get() = listOf(student, primaryName, primaryMobile, primaryEmail, secondaryName, secondaryMobile, secondaryEmail).any { it.isNotBlank() }
}

internal fun validateParentEditor(draft: ParentEditorDraft): ParentEditorValidationErrors {
    fun normalizedMobile(value: String): String {
        val digits = value.filter(Char::isDigit)
        return if (digits.length == 12 && digits.startsWith("91")) digits.removePrefix("91") else digits
    }
    fun mobileError(value: String, requiredMessage: String): String {
        val normalized = normalizedMobile(value.trim())
        return when {
            normalized.isBlank() -> requiredMessage
            !isValidMobileNumber(normalized) -> "Enter a valid mobile number."
            else -> ""
        }
    }
    fun emailError(value: String, required: Boolean): String {
        val normalized = value.trim()
        return when {
            normalized.isBlank() && required -> "Primary contact email is required."
            normalized.isBlank() -> ""
            !isValidEmail(normalized) -> "Enter a valid email address."
            else -> ""
        }
    }

    val hasSecondary = listOf(draft.secondaryContactName, draft.secondaryContactMobile, draft.secondaryContactEmail).any { it.isNotBlank() }
    return ParentEditorValidationErrors(
        student = if (draft.id.isBlank() && draft.selectedStudentId.trim().isBlank()) "Student is required." else "",
        primaryName = if (draft.primaryContactName.trim().isBlank()) "Primary contact name is required." else "",
        primaryMobile = mobileError(draft.primaryContactMobile, "Primary contact mobile number is required."),
        primaryEmail = emailError(draft.primaryContactEmail, required = draft.id.isBlank()),
        secondaryName = if (hasSecondary && draft.secondaryContactName.trim().isBlank()) "Secondary contact name is required when secondary contact is provided." else "",
        secondaryMobile = if (hasSecondary) mobileError(draft.secondaryContactMobile, "Secondary contact mobile number is required when secondary contact is provided.") else "",
        secondaryEmail = emailError(draft.secondaryContactEmail, required = false)
    )
}

data class ParentFilterCriteria(
    val parentName: String = "",
    val studentQuery: String = "",
    val classSectionId: String = "",
    val className: String = "",
    val section: String = "",
    val primaryRelationshipCode: String = "",
    val secondaryRelationshipCode: String = "",
    val mobile: String = "",
    val createdFrom: String = "",
    val createdTo: String = ""
)

enum class ParentSortField(val apiField: String) {
    ADMISSION_NO("admission_number"),
    STUDENT_DETAILS("name"),
    PRIMARY_CONTACT_NAME("name"),
    PRIMARY_CONTACT_MOBILE("parents"),
    RELATIONSHIP("parents")
}

data class ParentPageUi(
    val items: List<ParentRowUi> = emptyList(),
    val totalCount: Int = 0,
    val startIndex: Int = 0,
    val endIndex: Int = 0,
    val pageCount: Int = 1
)

internal fun parentFilterLabels(filters: ParentFilterCriteria): List<String> = buildList {
    if (filters.parentName.isNotBlank()) add("Parent: ${filters.parentName.trim()}")
    if (filters.studentQuery.isNotBlank()) add("Student: ${filters.studentQuery.trim()}")
    if (filters.className.isNotBlank()) add("Class: ${filters.className.trim()}")
    if (filters.section.isNotBlank()) add("Section: ${filters.section.trim()}")
    if (filters.primaryRelationshipCode.isNotBlank()) add("Primary relation: ${filters.primaryRelationshipCode.trim().toRelationshipLabel()}")
    if (filters.secondaryRelationshipCode.isNotBlank()) add("Secondary relation: ${filters.secondaryRelationshipCode.trim().toRelationshipLabel()}")
    if (filters.mobile.isNotBlank()) add("Mobile: ${filters.mobile.trim()}")
    if (filters.createdFrom.isNotBlank() || filters.createdTo.isNotBlank()) {
        val from = filters.createdFrom.ifBlank { "Any" }
        val to = filters.createdTo.ifBlank { "Any" }
        add("Created: $from to $to")
    }
}

private fun String.toRelationshipLabel(): String =
    SystemLookupOptions.label("PARENT_RELATIONSHIP", this)

internal fun sampleParentStudentOptions(): List<ParentStudentOptionUi> = sampleParentRows().map { row ->
    ParentStudentOptionUi(
        id = row.studentId,
        admissionNo = row.admissionNo,
        studentName = row.studentName,
        className = row.className,
        section = row.section,
        rollNo = row.rollNo,
        statusCode = row.statusCode
    )
}

internal fun sampleParentRows(): List<ParentRowUi> {
    val seedRows = listOf(
        ParentRowUi(
            id = "parent-001",
            admissionNo = "ADM001",
            studentId = "student-001",
            studentName = "Aarav Kumar",
            className = "5",
            section = "A",
            rollNo = "12",
            primaryContactName = "Parent Contact",
            primaryContactMobile = "9876543210",
            primaryContactEmail = "parent@example.com",
            primaryRelationshipCode = SchoolKeys.V_REL_FATHER,
            secondaryContactName = "Meera Kumar",
            secondaryContactMobile = "9876543211",
            secondaryContactEmail = "meera.kumar@example.com",
            secondaryRelationshipCode = SchoolKeys.V_REL_MOTHER,
            statusCode = SchoolKeys.V_STATUS_ACTIVE,
            createdBy = "School Administrator",
            createdAtText = "15 May 2024, 10:30 AM",
            updatedBy = "School Administrator",
            updatedAtText = "28 May 2024, 04:45 PM",
            createdAtMillis = 1715778600000,
            updatedAtMillis = 1716893100000
        ),
        ParentRowUi(
            id = "parent-002",
            admissionNo = "ADM002",
            studentId = "student-002",
            studentName = "Diya Joseph",
            className = "4",
            section = "B",
            rollNo = "08",
            primaryContactName = "Joseph Pai",
            primaryContactMobile = "9876543220",
            primaryContactEmail = "joseph.pai@example.com",
            primaryRelationshipCode = SchoolKeys.V_REL_FATHER,
            secondaryContactName = "Anita Pai",
            secondaryContactMobile = "9876543221",
            secondaryContactEmail = "anita.pai@example.com",
            secondaryRelationshipCode = SchoolKeys.V_REL_MOTHER,
            statusCode = SchoolKeys.V_STATUS_ACTIVE,
            createdBy = "School Administrator",
            createdAtText = "14 May 2024, 11:15 AM",
            updatedBy = "School Administrator",
            updatedAtText = "28 May 2024, 04:10 PM",
            createdAtMillis = 1715685300000,
            updatedAtMillis = 1716891000000
        ),
        ParentRowUi(
            id = "parent-003",
            admissionNo = "ADM003",
            studentId = "student-003",
            studentName = "Rohan Pai",
            className = "6",
            section = "A",
            rollNo = "18",
            primaryContactName = "Rahul Rao",
            primaryContactMobile = "9876543230",
            primaryContactEmail = "rahul.rao@example.com",
            primaryRelationshipCode = SchoolKeys.V_REL_GUARDIAN,
            secondaryContactName = "Sangeeta Rao",
            secondaryContactMobile = "9876543231",
            secondaryContactEmail = "sangeeta.rao@example.com",
            secondaryRelationshipCode = SchoolKeys.V_REL_MOTHER,
            statusCode = SchoolKeys.V_STATUS_ACTIVE,
            createdBy = "School Administrator",
            createdAtText = "13 May 2024, 09:05 AM",
            updatedBy = "School Administrator",
            updatedAtText = "27 May 2024, 03:35 PM",
            createdAtMillis = 1715591100000,
            updatedAtMillis = 1716809700000
        ),
        ParentRowUi(
            id = "parent-004",
            admissionNo = "ADM004",
            studentId = "student-004",
            studentName = "Meera Nair",
            className = "7",
            section = "C",
            rollNo = "21",
            primaryContactName = "John Mathew",
            primaryContactMobile = "9876543240",
            primaryContactEmail = "john.mathew@example.com",
            primaryRelationshipCode = SchoolKeys.V_REL_FATHER,
            secondaryContactName = "Mary Mathew",
            secondaryContactMobile = "9876543241",
            secondaryContactEmail = "mary.mathew@example.com",
            secondaryRelationshipCode = SchoolKeys.V_REL_MOTHER,
            statusCode = SchoolKeys.V_STATUS_ACTIVE,
            createdBy = "School Administrator",
            createdAtText = "13 May 2024, 09:40 AM",
            updatedBy = "School Administrator",
            updatedAtText = "27 May 2024, 03:50 PM",
            createdAtMillis = 1715593200000,
            updatedAtMillis = 1716810600000
        ),
        ParentRowUi(
            id = "parent-005",
            admissionNo = "ADM005",
            studentId = "student-005",
            studentName = "Sneha Kumar",
            className = "8",
            section = "B",
            rollNo = "03",
            primaryContactName = "Sneha Kumar",
            primaryContactMobile = "9876543250",
            primaryContactEmail = "sneha.kumar@example.com",
            primaryRelationshipCode = SchoolKeys.V_REL_MOTHER,
            secondaryContactName = "Suresh Kumar",
            secondaryContactMobile = "9876543251",
            secondaryContactEmail = "suresh.kumar@example.com",
            secondaryRelationshipCode = SchoolKeys.V_REL_FATHER,
            statusCode = SchoolKeys.V_STATUS_ACTIVE,
            createdBy = "School Administrator",
            createdAtText = "12 May 2024, 10:20 AM",
            updatedBy = "School Administrator",
            updatedAtText = "27 May 2024, 05:05 PM",
            createdAtMillis = 1715509200000,
            updatedAtMillis = 1716815100000
        ),
        ParentRowUi(
            id = "parent-006",
            admissionNo = "ADM006",
            studentId = "student-006",
            studentName = "Kiran Joseph",
            className = "9",
            section = "A",
            rollNo = "09",
            primaryContactName = "Kiran Joseph",
            primaryContactMobile = "9876543260",
            primaryContactEmail = "kiran.joseph@example.com",
            primaryRelationshipCode = SchoolKeys.V_REL_FATHER,
            secondaryContactName = "Leena Joseph",
            secondaryContactMobile = "9876543261",
            secondaryContactEmail = "leena.joseph@example.com",
            secondaryRelationshipCode = SchoolKeys.V_REL_MOTHER,
            statusCode = SchoolKeys.V_STATUS_ACTIVE,
            createdBy = "School Administrator",
            createdAtText = "11 May 2024, 08:55 AM",
            updatedBy = "School Administrator",
            updatedAtText = "26 May 2024, 06:15 PM",
            createdAtMillis = 1715427300000,
            updatedAtMillis = 1716725700000
        ),
        ParentRowUi(
            id = "parent-007",
            admissionNo = "ADM007",
            studentId = "student-007",
            studentName = "Aditya Rao",
            className = "10",
            section = "A",
            rollNo = "17",
            primaryContactName = "Aditya Rao",
            primaryContactMobile = "9876543270",
            primaryContactEmail = "aditya.rao@example.com",
            primaryRelationshipCode = SchoolKeys.V_REL_FATHER,
            secondaryContactName = "Anjali Rao",
            secondaryContactMobile = "9876543271",
            secondaryContactEmail = "anjali.rao@example.com",
            secondaryRelationshipCode = SchoolKeys.V_REL_MOTHER,
            statusCode = SchoolKeys.V_STATUS_INACTIVE,
            createdBy = "School Administrator",
            createdAtText = "10 May 2024, 11:10 AM",
            updatedBy = "School Administrator",
            updatedAtText = "26 May 2024, 05:45 PM",
            createdAtMillis = 1715349600000,
            updatedAtMillis = 1716723900000
        )
    )

    val surnames = listOf("Kumar", "Reddy", "Nair", "Pai", "Rao", "Joseph", "Nair", "Sharma", "Patel", "Singh")
    val firstNames = listOf("Arun", "Aarush", "Anaya", "Ishaan", "Mira", "Kabir", "Diya", "Aarav", "Veda", "Rohan")
    val contactPrefixes = listOf("Parent", "Joseph", "Rahul", "John", "Sneha", "Kiran", "Aditya", "Priya", "Suresh", "Meera")
    val sections = listOf("A", "B", "C", "D")

    val syntheticRows = buildList {
        for (index in 8..1248) {
            val serial = index.toString().padStart(3, '0')
            val studentName = "${firstNames[(index - 1) % firstNames.size]} ${surnames[(index - 1) % surnames.size]}"
            val className = (((index - 1) % 10) + 1).toString()
            val section = sections[(index - 1) % sections.size]
            val rollNo = (((index - 1) % 30) + 1).toString().padStart(2, '0')
            val primaryName = "${contactPrefixes[(index - 1) % contactPrefixes.size]} ${surnames[(index - 1) % surnames.size]}"
            val secondaryName = "${firstNames[(index + 2) % firstNames.size]} ${surnames[(index + 3) % surnames.size]}"
            val active = index <= 1221
            val createdMillis = 1715000000000 + (index * 86_400_000L)
            add(
                ParentRowUi(
                    id = "parent-$serial",
                    admissionNo = "ADM$serial",
                    studentId = "student-$serial",
                    studentName = studentName,
                    className = className,
                    section = section,
                    rollNo = rollNo,
                    primaryContactName = primaryName,
                    primaryContactMobile = "98765${(43210 + index).toString().takeLast(5)}",
                    primaryContactEmail = "${primaryName.lowercase().replace(' ', '.')}@example.com",
                    primaryRelationshipCode = when (index % 3) {
                        0 -> SchoolKeys.V_REL_FATHER
                        1 -> SchoolKeys.V_REL_MOTHER
                        else -> SchoolKeys.V_REL_GUARDIAN
                    },
                    secondaryContactName = secondaryName,
                    secondaryContactMobile = "98764${(53100 + index).toString().takeLast(5)}",
                    secondaryContactEmail = "${secondaryName.lowercase().replace(' ', '.')}@example.com",
                    secondaryRelationshipCode = when ((index + 1) % 3) {
                        0 -> SchoolKeys.V_REL_FATHER
                        1 -> SchoolKeys.V_REL_MOTHER
                        else -> SchoolKeys.V_REL_GUARDIAN
                    },
                    statusCode = if (active) SchoolKeys.V_STATUS_ACTIVE else SchoolKeys.V_STATUS_INACTIVE,
                    createdBy = "School Administrator",
                    createdAtText = "15 May 2024, 10:30 AM",
                    updatedBy = "School Administrator",
                    updatedAtText = "28 May 2024, 04:45 PM",
                    createdAtMillis = createdMillis,
                    updatedAtMillis = createdMillis + 5_400_000L
                )
            )
        }
    }

    return seedRows + syntheticRows
}
